/**
 * Generic-CSV parser — Slice 2 MVP adapter.
 *
 * Input shape: a flat CSV where each row represents ONE job/line
 * under one repair order. Multiple rows with the same RO id roll
 * up into a single ParsedRepairOrder with multiple ParsedJob
 * children. Customers and vehicles roll up the same way.
 *
 * When an `externalRepairOrderId` is missing from the file, we
 * synthesize a stable surrogate from `(roNumber || lineN, openedAt)`
 * so subsequent re-imports of the same file produce the same hash
 * (idempotent dedupe). Similarly for customer/vehicle ids.
 */

import type {
  ParsedBatch,
  ParsedCustomer,
  ParsedJob,
  ParsedRepairOrder,
  ParsedVehicle,
  ParserWarning,
} from '../types';
import {
  hashCustomer,
  hashJob,
  hashRepairOrder,
  hashVehicle,
} from '../hash/rowHash';
import {
  buildColumnMap,
  REQUIRED_FIELDS,
  type ColumnMap,
  type LogicalField,
} from './columnMap';
import {
  coerceBoolean,
  coerceCents,
  coerceHours,
  coerceInteger,
  coerceString,
  coerceTimestamp,
  coerceVin,
} from './coerce';
import { tokenizeCsv, type TokenizeOptions } from './csvTokenizer';

export interface GenericCsvParseOptions extends TokenizeOptions {
  /**
   * Force the column map (skip header sniffing). Used after the UI
   * remap dialog confirms a manual mapping.
   */
  forcedColumnMap?: ColumnMap;
}

export interface GenericCsvParseResult extends ParsedBatch {
  /**
   * Headers as they appeared in the source file (after BOM strip)
   * — the UI uses this to render the remap dialog.
   */
  headers: string[];
  columnMap: ColumnMap;
  unmappedHeaders: Array<{ index: number; header: string }>;
  /**
   * Missing REQUIRED fields. When non-empty the import job stays in
   * "needs remap" status — no rows are written downstream.
   */
  missingRequiredFields: LogicalField[];
}

function cell(row: string[], idx: number | undefined): string | null {
  if (idx === undefined) return null;
  return row[idx] ?? null;
}

/** Stable surrogate id for a missing external id. */
function surrogate(prefix: string, parts: Array<string | number | null>): string {
  const joined = parts.map((p) => (p === null || p === undefined ? '' : String(p))).join('|');
  return `${prefix}::${joined}`;
}

export function parseGenericCsv(
  text: string,
  options: GenericCsvParseOptions = {},
): GenericCsvParseResult {
  const rows = tokenizeCsv(text, options);
  const warnings: ParserWarning[] = [];

  if (rows.length === 0) {
    return {
      customers: [],
      vehicles: [],
      repairOrders: [],
      jobs: [],
      sourceFormat: 'generic-csv',
      headers: [],
      columnMap: {},
      unmappedHeaders: [],
      missingRequiredFields: [...REQUIRED_FIELDS],
      warnings: [{ rowNumber: 0, field: null, message: 'File contained no rows.' }],
    };
  }

  const headerRow = rows[0];
  const headers = headerRow.cells.map((c) => c ?? '');

  const { map: detectedMap, unmappedHeaders } = buildColumnMap(headers);
  const map: ColumnMap = options.forcedColumnMap ?? detectedMap;

  const missingRequiredFields = REQUIRED_FIELDS.filter((f) => map[f] === undefined);

  const customers = new Map<string, ParsedCustomer>();
  const vehicles = new Map<string, ParsedVehicle>();
  const repairOrders = new Map<string, { ro: ParsedRepairOrder; nextLine: number }>();
  const jobs: ParsedJob[] = [];

  // If required fields are missing, we still tokenize + emit the
  // empty batch so the UI can render the remap dialog. We do NOT
  // try to coerce data — silently misparsed values are worse than
  // an explicit "needs mapping" status.
  if (missingRequiredFields.length === 0) {
    for (let i = 1; i < rows.length; i++) {
      const { rowNumber, cells } = rows[i];
      // Skip fully-empty rows
      if (cells.every((c) => c.trim().length === 0)) continue;

      const openedAt = coerceTimestamp(cell(cells, map.openedAt));
      if (openedAt === null) {
        warnings.push({
          rowNumber,
          field: 'openedAt',
          message: 'Could not parse opened-at date; row skipped.',
        });
        continue;
      }
      const description = coerceString(cell(cells, map.jobDescription));
      if (description === null) {
        warnings.push({
          rowNumber,
          field: 'jobDescription',
          message: 'Empty job description; row skipped.',
        });
        continue;
      }

      // ---------- Customer ----------
      const customerEmail = coerceString(cell(cells, map.customerEmail));
      const customerPhone = coerceString(cell(cells, map.customerPhone));
      const customerFirstName = coerceString(cell(cells, map.customerFirstName));
      const customerLastName = coerceString(cell(cells, map.customerLastName));
      const customerCompanyName = coerceString(cell(cells, map.customerCompanyName));
      const externalCustomerId =
        coerceString(cell(cells, map.externalCustomerId)) ??
        surrogate('cust', [
          customerEmail?.toLowerCase() ?? null,
          customerPhone,
          customerLastName,
          customerFirstName,
          customerCompanyName,
        ]);

      const customerHash = hashCustomer({
        externalCustomerId,
        firstName: customerFirstName,
        lastName: customerLastName,
        companyName: customerCompanyName,
        email: customerEmail,
        phone: customerPhone,
      });

      if (!customers.has(externalCustomerId)) {
        customers.set(externalCustomerId, {
          externalCustomerId,
          firstName: customerFirstName,
          lastName: customerLastName,
          companyName: customerCompanyName,
          email: customerEmail,
          phone: customerPhone,
          addressLine1: coerceString(cell(cells, map.customerAddressLine1)),
          addressLine2: coerceString(cell(cells, map.customerAddressLine2)),
          city: coerceString(cell(cells, map.customerCity)),
          region: coerceString(cell(cells, map.customerRegion)),
          postalCode: coerceString(cell(cells, map.customerPostalCode)),
          country: coerceString(cell(cells, map.customerCountry)),
          notes: null,
          rowHash: customerHash,
        });
      }

      // ---------- Vehicle ----------
      const vin = coerceVin(cell(cells, map.vehicleVin));
      const plate = coerceString(cell(cells, map.vehiclePlate));
      const plateState = coerceString(cell(cells, map.vehiclePlateState));
      const year = coerceInteger(cell(cells, map.vehicleYear));
      const make = coerceString(cell(cells, map.vehicleMake));
      const model = coerceString(cell(cells, map.vehicleModel));
      const externalVehicleId =
        coerceString(cell(cells, map.externalVehicleId)) ??
        surrogate('veh', [
          vin,
          plate?.toUpperCase() ?? null,
          plateState?.toUpperCase() ?? null,
          externalCustomerId,
          year,
          make,
          model,
        ]);
      const vehicleHash = hashVehicle({
        externalVehicleId,
        vin,
        licensePlate: plate,
        licenseState: plateState,
        year,
        make,
        model,
      });
      if (!vehicles.has(externalVehicleId)) {
        vehicles.set(externalVehicleId, {
          externalVehicleId,
          externalCustomerId,
          vin,
          licensePlate: plate,
          licenseState: plateState,
          year,
          make,
          model,
          trim: coerceString(cell(cells, map.vehicleTrim)),
          engine: coerceString(cell(cells, map.vehicleEngine)),
          color: coerceString(cell(cells, map.vehicleColor)),
          odometerLast:
            coerceInteger(cell(cells, map.vehicleOdometerLast)) ??
            coerceInteger(cell(cells, map.odometerOut)) ??
            null,
          notes: null,
          rowHash: vehicleHash,
        });
      }

      // ---------- Repair order ----------
      const roNumber = coerceString(cell(cells, map.roNumber));
      const externalRepairOrderId =
        coerceString(cell(cells, map.externalRepairOrderId)) ??
        surrogate('ro', [roNumber, openedAt, externalCustomerId, externalVehicleId]);

      const totalCents = coerceCents(cell(cells, map.totalCents));

      let bucket = repairOrders.get(externalRepairOrderId);
      if (!bucket) {
        const ro: ParsedRepairOrder = {
          externalRepairOrderId,
          roNumber,
          externalCustomerId,
          externalVehicleId,
          openedAt,
          promisedAt: coerceTimestamp(cell(cells, map.promisedAt)),
          closedAt: coerceTimestamp(cell(cells, map.closedAt)),
          advisorDisplayName: coerceString(cell(cells, map.roAdvisorName)),
          externalAdvisorId: coerceString(cell(cells, map.roAdvisorId)),
          externalTechnicianId: coerceString(cell(cells, map.roTechnicianId)),
          sourceStatus: coerceString(cell(cells, map.sourceStatus)),
          odometerIn: coerceInteger(cell(cells, map.odometerIn)),
          odometerOut: coerceInteger(cell(cells, map.odometerOut)),
          subtotalCents: coerceCents(cell(cells, map.subtotalCents)),
          taxCents: coerceCents(cell(cells, map.taxCents)),
          discountCents: coerceCents(cell(cells, map.discountCents)),
          totalCents,
          rowHash: '',
          live_customer_id: null,
          live_vehicle_id: null,
        };
        ro.rowHash = hashRepairOrder({
          externalRepairOrderId,
          openedAt,
          totalCents,
          externalCustomerId,
          externalVehicleId,
        });
        bucket = { ro, nextLine: 1 };
        repairOrders.set(externalRepairOrderId, bucket);
      }

      // ---------- Job line ----------
      const explicitLineNumber = coerceInteger(cell(cells, map.jobLineNumber));
      const lineNumber = explicitLineNumber ?? bucket.nextLine;
      bucket.nextLine = Math.max(bucket.nextLine, lineNumber) + 1;

      const opCode = coerceString(cell(cells, map.jobOpCode));
      const laborCents = coerceCents(cell(cells, map.jobLaborCents));
      const partsCents = coerceCents(cell(cells, map.jobPartsCents));

      const jobHash = hashJob({
        repairOrderRowHash: bucket.ro.rowHash,
        lineNumber,
        description,
        opCode,
        laborCents,
        partsCents,
      });

      jobs.push({
        repairOrderRowHash: bucket.ro.rowHash,
        lineNumber,
        externalLineId: coerceString(cell(cells, map.externalLineId)),
        description,
        opCode,
        laborHours: coerceHours(cell(cells, map.jobLaborHours)),
        laborCents,
        partsCents,
        taxCents: coerceCents(cell(cells, map.jobTaxCents)),
        discountCents: coerceCents(cell(cells, map.jobDiscountCents)),
        externalTechnicianId: coerceString(cell(cells, map.jobTechnicianId)),
        technicianDisplayName: coerceString(cell(cells, map.jobTechnicianName)),
        isWarranty: coerceBoolean(cell(cells, map.jobIsWarranty)),
        isComeback: coerceBoolean(cell(cells, map.jobIsComeback)),
        rowHash: jobHash,
      });
    }
  }

  return {
    customers: Array.from(customers.values()),
    vehicles: Array.from(vehicles.values()),
    repairOrders: Array.from(repairOrders.values()).map((b) => b.ro),
    jobs,
    sourceFormat: 'generic-csv',
    headers,
    columnMap: map,
    unmappedHeaders,
    missingRequiredFields,
    warnings,
  };
}
